import { Link } from "react-router-dom";
import { ArrowRight, Camera, Heart, Users, Sparkles, Leaf, Home as HomeIcon, Lock, Printer, Usb, FileDigit, Fuel, Phone, MessageSquare } from "lucide-react";
import Reveal, { SectionHeading } from "@/components/Reveal";

const SESSIONS = [
  {
    icon: Camera,
    num: "01",
    titre: "Séance Portrait — Solo",
    texte: "Une séance en studio ou à l'extérieur, adaptée à votre personnalité et à votre projet.",
    forfaits: [["5", "75 $"], ["10", "100 $"], ["15", "125 $"], ["20", "150 $"]],
    note: "Studio ou extérieur, selon le projet.",
  },
  {
    icon: Heart,
    num: "02",
    titre: "Séance Couple",
    texte: "Des images naturelles et authentiques pour raconter votre complicité et vos moments à deux.",
    forfaits: [["10", "100 $"], ["15", "125 $"], ["20", "150 $"], ["25", "175 $"]],
    note: "En studio ou à l'extérieur.",
  },
  {
    icon: Users,
    num: "03",
    titre: "Séance Famille",
    texte: "Des souvenirs chaleureux pour toute la famille, petits et grands.",
    forfaits: [["10", "125 $"], ["15", "150 $"], ["20", "175 $"], ["25", "200 $"]],
    note: "En studio ou à l'extérieur. Les petits animaux de compagnie peuvent être acceptés au studio selon le projet et les possibilités du décor.",
  },
];

const INFOS = [
  {
    icon: Lock,
    num: "07",
    titre: "Galerie privée en ligne",
    texte: "Vos photographies finales sont livrées dans une galerie privée protégée par mot de passe. Vous pouvez télécharger directement vos photographies finales à partir de cette galerie. Le mot de passe et les informations d'accès vous sont transmis lors de la livraison.",
  },
  {
    icon: Usb,
    num: "09",
    titre: "Livraison sur clé USB",
    texte: "Une livraison sur clé USB est disponible sur demande. La clé USB est facturée séparément. La livraison physique peut également être possible selon la distance. Pour une demande comprenant un grand volume de fichiers, le coût du support peut varier selon la quantité de données.",
  },
  {
    icon: FileDigit,
    num: "10",
    titre: "Fichiers originaux",
    texte: "Les photographies finales retouchées sont livrées selon le forfait choisi. La remise des fichiers originaux RAW peut être demandée pour certains projets et doit être convenue avant la séance. Cette option peut entraîner des frais supplémentaires selon le projet et le volume de fichiers.",
  },
  {
    icon: Fuel,
    num: "11",
    titre: "Frais de déplacement",
    texte: "Les séances réalisées dans mon secteur et les environs sont sans frais de déplacement. Pour une séance nécessitant un déplacement plus important, des frais peuvent s'appliquer selon la distance. Le montant des frais de déplacement est toujours confirmé avec vous avant la séance.",
  },
];

const FORMATS = ["8 × 10", "8 × 12", "10 × 10", "11 × 14", "12 × 18", "14 × 20", "16 × 20"];

function TierRows({ forfaits, testid }) {
  return (
    <div className="mt-6 border-t border-[#D4AF37]/15" data-testid={testid}>
      {forfaits.map(([n, prix]) => (
        <div key={n} className="flex items-baseline justify-between py-3 border-b border-[#D4AF37]/10">
          <span className="text-slate-300 text-sm">{n} photos retouchées</span>
          <span className="font-serif text-xl gold-text">{prix}</span>
        </div>
      ))}
    </div>
  );
}

export default function Pricing() {
  return (
    <main data-testid="pricing-page" className="pt-20">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <SectionHeading number="01" eyebrow="Tarifs et séances" title="Des forfaits selon vos souvenirs"
                        subtitle="Le prix dépend du nombre de photographies finales retouchées que vous choisissez — jamais du temps passé. Les séances peuvent être réalisées en studio ou à l'extérieur, selon votre projet." />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16" data-testid="sessions-grid">
          {SESSIONS.map((s, i) => (
            <Reveal key={s.titre} delay={i * 0.12}>
              <div className="gold-card p-8 h-full flex flex-col" data-testid={`session-card-${s.num}`}>
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 border border-[#D4AF37]/40 rotate-45 flex items-center justify-center">
                    <s.icon size={20} className="text-[#D4AF37] -rotate-45" />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C6D46]">{s.num}</span>
                </div>
                <h3 className="font-serif text-2xl text-[#F8F9FA] mt-8">{s.titre}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mt-3">{s.texte}</p>
                <TierRows forfaits={s.forfaits} testid={`session-tiers-${s.num}`} />
                <p className="text-[#D4AF37] text-xs leading-relaxed mt-5">{s.note}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* 04 — Projet sur mesure */}
        <Reveal className="mt-6">
          <div className="gold-card p-8 lg:p-12" data-testid="custom-project-card">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 border border-[#D4AF37]/40 rotate-45 flex items-center justify-center">
                    <Sparkles size={20} className="text-[#D4AF37] -rotate-45" />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C6D46]">04</span>
                </div>
                <h3 className="font-serif text-2xl lg:text-3xl text-[#F8F9FA] mt-8">Projet sur mesure</h3>
                <p className="text-slate-400 text-sm leading-relaxed mt-3">
                  Pour les projets particuliers qui demandent une approche personnalisée. Cela peut inclure notamment :
                </p>
                <ul className="mt-5 space-y-2 text-sm text-slate-300" data-testid="custom-project-list">
                  {["Photographie avec animaux", "Projets artistiques ou créatifs", "Véhicules et motos", "Projets personnels", "Événements ou autres demandes particulières"].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <span className="text-[#D4AF37] text-[10px]">◆</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:border-l lg:border-[#D4AF37]/15 lg:pl-10 flex flex-col justify-center">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Prix</p>
                <p className="font-serif text-3xl gold-text mt-3" data-testid="custom-project-price">Sur devis</p>
                <p className="text-slate-400 text-sm leading-relaxed mt-4">
                  Le prix est établi selon le projet, le nombre de sujets, le lieu, le déplacement et le nombre de
                  photographies souhaitées.
                </p>
                <p className="text-slate-500 text-xs leading-relaxed mt-5 border-t border-[#D4AF37]/10 pt-5">
                  Pour les animaux au studio : le studio est un studio privé à domicile. Les petits animaux de
                  compagnie peuvent être accueillis selon le projet et les possibilités du décor. Les gros animaux,
                  comme les chevaux, doivent être photographiés à l'extérieur.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* 05 + 06 — Automne */}
      <section className="bg-[#0D0D10] border-y border-[#D4AF37]/10 py-24" data-testid="autumn-pricing-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="gold-card p-8 lg:p-12 h-full" data-testid="autumn-outdoor-card">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 border border-[#D4AF37]/40 rotate-45 flex items-center justify-center">
                  <Leaf size={20} className="text-[#D4AF37] -rotate-45" />
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C6D46]">05</span>
              </div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37] mt-8">Spécial couleurs d'automne — Extérieur</p>
              <h3 className="font-serif text-2xl lg:text-3xl text-[#F8F9FA] mt-3">Les couleurs changent. Vos souvenirs restent.</h3>
              <p className="text-slate-400 text-sm leading-relaxed mt-4">
                Séances photo extérieures dans les beaux décors d'automne de la Montérégie et des environs, selon
                l'évolution des couleurs.
              </p>
              <TierRows forfaits={[["10", "100 $"], ["15", "125 $"], ["20", "150 $"]]} testid="autumn-tiers" />
              <p className="text-slate-400 text-xs leading-relaxed mt-5">
                La période prévue est de la mi-octobre à la mi-novembre 2026, selon l'évolution des couleurs dans les
                arbres. Le déplacement est inclus dans Saint-Jean-sur-Richelieu et les environs immédiats. Pour les
                endroits plus éloignés, des frais de déplacement peuvent s'appliquer selon la distance — le montant est
                toujours confirmé avec vous avant la séance.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="gold-card p-8 lg:p-12 h-full" data-testid="autumn-studio-card">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 border border-[#D4AF37]/40 rotate-45 flex items-center justify-center">
                  <HomeIcon size={20} className="text-[#D4AF37] -rotate-45" />
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C6D46]">06</span>
              </div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37] mt-8">Automne au studio</p>
              <h3 className="font-serif text-2xl lg:text-3xl text-[#F8F9FA] mt-3">Studio Quatre Saisons</h3>
              <p className="text-slate-400 text-sm leading-relaxed mt-4">
                Le décor automnal du Studio Quatre Saisons sera disponible à partir du 5 octobre 2026. Découvrez une
                ambiance chaleureuse et créative pour vos portraits, vos séances de couple, vos séances famille et
                certains petits animaux de compagnie.
              </p>
              <div className="mt-6 border-t border-[#D4AF37]/15 pt-6 space-y-3 text-sm">
                <p className="text-slate-300">Les forfaits réguliers selon le nombre de photographies s'appliquent.</p>
                <p className="text-[#F3E5AB] flex items-center gap-3">
                  <span className="text-[#D4AF37] text-[10px]">◆</span>
                  Réservations ouvertes pour les séances à partir du 5 octobre 2026
                </p>
              </div>
              <Link to="/services" data-testid="autumn-studio-link" className="btn-outline-gold mt-8 !px-6 !py-3">
                Découvrir le studio <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 07 à 11 — Infos pratiques */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="infos-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {INFOS.slice(0, 1).map((info) => (
            <Reveal key={info.num}>
              <InfoCard info={info} />
            </Reveal>
          ))}
          <Reveal delay={0.12}>
            <div className="gold-card p-8 h-full" data-testid="info-card-08">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 border border-[#D4AF37]/40 rotate-45 flex items-center justify-center">
                  <Printer size={20} className="text-[#D4AF37] -rotate-45" />
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C6D46]">08</span>
              </div>
              <h3 className="font-serif text-2xl text-[#F8F9FA] mt-8">Impressions & formats</h3>
              <p className="text-slate-400 text-sm leading-relaxed mt-4">
                Vous souhaitez faire imprimer vos photographies ? Je peux préparer vos fichiers selon le format
                d'impression désiré afin que la photographie soit correctement adaptée au format choisi et au cadrage
                nécessaire.
              </p>
              <div className="flex flex-wrap gap-2 mt-5" data-testid="print-formats">
                {FORMATS.map((f) => (
                  <span key={f} className="border border-[#D4AF37]/30 text-[#F3E5AB] text-xs px-4 py-2">{f}</span>
                ))}
              </div>
              <p className="text-slate-500 text-xs leading-relaxed mt-5">
                Le prix d'une impression peut varier selon le format, le papier et la finition choisis. Le prix sera
                confirmé avant l'impression.
              </p>
            </div>
          </Reveal>
          {INFOS.slice(1).map((info, i) => (
            <Reveal key={info.num} delay={(i + 2) % 2 * 0.12}>
              <InfoCard info={info} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* 12 — Pour me rejoindre */}
      <section className="bg-[#0D0D10] border-t border-[#D4AF37]/10 py-24" data-testid="contact-cta-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]">12 — Pour me rejoindre</p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-4">Parlons de votre projet</h2>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed mt-6" data-testid="contact-cta-text">
              Vous pouvez me joindre par téléphone au <a href="tel:+15147818360" className="text-[#F3E5AB] hover:text-[#D4AF37] transition-colors">514-781-8360</a>.
              Si je ne peux pas répondre immédiatement, n'hésitez pas à m'envoyer un message texte. Lorsque je suis en
              déplacement ou en séance, je ne suis pas toujours en mesure de répondre aux appels, mais je prends
              connaissance de mes messages dès que possible.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <a href="tel:+15147818360" data-testid="pricing-call-button" className="btn-outline-gold">
                <Phone size={15} /> 514-781-8360
              </a>
              <a href="sms:+15147818360" data-testid="pricing-texto-button" className="btn-outline-gold">
                <MessageSquare size={15} /> Message texte
              </a>
              <Link to="/contact" data-testid="pricing-contact-button" className="btn-gold">
                Demander une soumission <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

function InfoCard({ info }) {
  return (
    <div className="gold-card p-8 h-full" data-testid={`info-card-${info.num}`}>
      <div className="flex items-center justify-between">
        <div className="w-12 h-12 border border-[#D4AF37]/40 rotate-45 flex items-center justify-center">
          <info.icon size={20} className="text-[#D4AF37] -rotate-45" />
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C6D46]">{info.num}</span>
      </div>
      <h3 className="font-serif text-2xl text-[#F8F9FA] mt-8">{info.titre}</h3>
      <p className="text-slate-400 text-sm leading-relaxed mt-4">{info.texte}</p>
    </div>
  );
}
